import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authService, Usuari } from '../services/api';

interface AuthContextValue {
  usuari: Usuari | null;
  token: string | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  teRol: (codi: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [usuari, setUsuari] = useState<Usuari | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('avaluam_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('avaluam_token');
    setToken(null);
    setUsuari(null);
  }, []);

  useEffect(() => {
    let active = true;

    async function carregarSessio() {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const usuariActual = await authService.me();
        if (active) setUsuari(usuariActual);
      } catch (error) {
        if (active) logout();
      } finally {
        if (active) setLoading(false);
      }
    }

    carregarSessio();

    return () => {
      active = false;
    };
  }, [token, logout]);

  const login = useCallback(async (username: string, password: string) => {
    const response = await authService.login(username, password);
    localStorage.setItem('avaluam_token', response.token);
    setToken(response.token);
    setUsuari(response.usuari);
  }, []);

  const teRol = useCallback(
    (codi: string) => usuari?.rols.some((rol) => rol.codi === codi) || false,
    [usuari]
  );

  const value = useMemo(
    () => ({ usuari, token, loading, login, logout, teRol }),
    [usuari, token, loading, login, logout, teRol]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth s'ha d'utilitzar dins AuthProvider");
  }

  return context;
}
